import type { DragEvent } from 'react'
import type { SRT_Header, SRT_RowData, SRT_TableInstance } from '../../types'

import React from 'react'

import { TableHead } from '@/shared/components/ui/table'

import { cn } from '../../lib/utils'
import { getHeaderPinningStyles } from '../../utils/utils'
import { SRT_ColumnActionMenu } from '../menus/SRT_ColumnActionMenu'
import { SRT_TableHeadCellGrabHandle } from './SRT_TableHeadCellGrabHandle'
import { SRT_TableHeadCellSortLabel } from './SRT_TableHeadCellSortLabel'
import { SRT_TableHeadLabel } from './SRT_TableHeadLabel'

export const SRT_TableHeadCell = <T extends SRT_RowData>({
	header,
	table,
	staticColumnIndex,
	options,
}: {
	staticColumnIndex: number
	options: {
		isPinned: boolean | 'left' | 'right'
		left: number
		right: number
	}
	header: SRT_Header<T>
	table: SRT_TableInstance<T>
}) => {
	const {
		getState,
		refs: { tableHeadCellRefs },
		setHoveredColumn,
		options: {
			divideX,
			divideY,
			enableColumnActions,
			enableColumnDragging,
			enableColumnOrdering,
			enableGrouping,
			layoutMode,
		},
	} = table

	const { grouping, draggingColumn, hoveredColumn } = getState()
	const { isPinned, left, right } = options
	const { column } = header
	const { columnDef } = column
	const { columnDefType } = columnDef
	const showDragHandle =
		enableColumnDragging !== false &&
		columnDef.enableColumnDragging !== false &&
		(enableColumnDragging ||
			(enableColumnOrdering && columnDef.enableColumnOrdering !== false) ||
			(enableGrouping &&
				columnDef.enableGrouping !== false &&
				!grouping.includes(column.id)))

	const showColumnActions =
		(enableColumnActions || column.columnDef.enableColumnActions) &&
		column.columnDef.enableColumnActions !== false

	const className = cn('h-9 bg-inherit px-2', {
		'border-r': divideX,
		'border-b': divideY,
		'border-l': isPinned === 'right',
		'border-r-2 border-dashed bg-accent': hoveredColumn?.id === column.id,
	})
	const handleDragEnter = () => {
		if (enableGrouping && hoveredColumn?.id === 'drop-zone') {
			setHoveredColumn(null)
		}
		if (enableColumnOrdering && draggingColumn && columnDefType !== 'group') {
			setHoveredColumn(columnDef.enableColumnOrdering !== false ? column : null)
		}
	}

	const handleDragOver = (e: DragEvent<HTMLTableCellElement>) => {
		if (columnDef.enableColumnOrdering !== false) {
			e.preventDefault()
		}
	}

	return (
		<TableHead
			data-testid={`mt-header-cell-${staticColumnIndex}`}
			data-columnid={header.id}
			colSpan={header.colSpan}
			key={`${header.id}`}
			className={className}
			style={getHeaderPinningStyles(header, {
				isPinned,
				left,
				right,
				layoutMode,
			})}
			onDragEnter={handleDragEnter}
			onDragOver={handleDragOver}
			ref={(node: HTMLTableCellElement) => {
				if (node) {
					tableHeadCellRefs.current = {
						...tableHeadCellRefs.current,
						[column.id]: node,
					}
				}
			}}
		>
			<div className='group whitespace-nowrap'>
				{header.isPlaceholder ? null : (
					<div className='flex items-center justify-between text-xs'>
						<div
							onClick={column.getToggleSortingHandler()}
							className={cn('flex items-center truncate', {
								'cursor-pointer': column.getCanSort(),
							})}
						>
							<SRT_TableHeadLabel table={table} header={header} />
							{column.getCanSort() && (
								<SRT_TableHeadCellSortLabel table={table} header={header} />
							)}
						</div>
						{columnDefType !== 'group' && (
							<div className='flex items-center whitespace-nowrap'>
								{showDragHandle && (
									<SRT_TableHeadCellGrabHandle
										className='hidden group-hover:flex'
										column={column}
										table={table}
										tableHeadCellRef={{
											current: tableHeadCellRefs.current?.[column.id] ?? null,
										}}
									/>
								)}
								{showColumnActions && (
									<SRT_ColumnActionMenu table={table} header={header} />
								)}
							</div>
						)}
					</div>
				)}
				{header.column.getCanResize() && (
					<SRT_TableHeadCellResizeHandle header={header} table={table} />
				)}
			</div>
		</TableHead>
	)
}

const SRT_TableHeadCellResizeHandle = <T extends SRT_RowData>({
	header,
	table,
}: {
	header: SRT_Header<T>
	table: SRT_TableInstance<T>
}) => (
	<div
		data-slot='resizable-handle'
		role='separator'
		{...{
			onDoubleClick: () => header.column.resetSize(),
			onMouseDown: header.getResizeHandler(),
			onTouchStart: header.getResizeHandler(),
			className: cn(
				'absolute top-0 right-0 bottom-0 flex w-0.5 cursor-col-resize items-center justify-center bg-border opacity-0 hover:opacity-100 focus-visible:ring-ring/30',
				{ 'bg-primary opacity-100': header.column.getIsResizing() }
			),
			style: {
				transform:
					table.options.columnResizeMode === 'onEnd' &&
					header.column.getIsResizing()
						? `translateX(${
								(table.options.columnResizeDirection === 'rtl' ? -1 : 1) *
								(table.getState().columnSizingInfo.deltaOffset ?? 0)
							}px)`
						: '',
			},
		}}
	/>
)
